import React, { useCallback, useMemo } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { EmptyState } from '@/components/ui/empty-state';
import { useProviderServices } from '@/hooks/use-services';
import { Service } from '@/types/service';
import { formatDuration } from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';

/** Chip escuro de "não agendável": fica escuro nos dois temas (como `bg-ink`). */
const INK = '#171717';

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120];

/** Ajustes ainda não salvos, por id de serviço. Só entra aqui o que o prestador tocou. */
export type ServiceSchedulingDraft = Record<
  string,
  { durationMinutes?: number | null; slotCapacity?: number }
>;

interface Props {
  value: ServiceSchedulingDraft;
  onChange: (
    id: string,
    patch: { durationMinutes?: number | null; slotCapacity?: number },
  ) => void;
}

interface RowProps {
  service: Service;
  onChange: Props['onChange'];
}

const ServiceRow = React.memo(function ServiceRow({ service, onChange }: RowProps) {
  const th = useThemedHex();
  const schedulable = !!service.durationMinutes;
  const capacity = service.slotCapacity ?? 1;

  return (
    <View
      className="gap-3 rounded-2xl border border-neutral-100 bg-card p-4"
      style={{ borderCurve: 'continuous' }}
    >
      <View className="flex-row items-center justify-between gap-3">
        <View className="min-w-0 flex-1">
          <ThemedText className="text-base font-semibold text-neutral-900" numberOfLines={1}>
            {service.name}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500">
            {schedulable
              ? `${formatDuration(service.durationMinutes)} · ${capacity > 1 ? `turma até ${capacity}` : 'individual'}`
              : 'Não agendável online'}
          </ThemedText>
        </View>
      </View>

      {/* Duração */}
      <View className="gap-1.5">
        <ThemedText className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
          Duração
        </ThemedText>
        <View className="flex-row flex-wrap gap-2">
          {DURATION_PRESETS.map((min) => {
            const active = service.durationMinutes === min;
            return (
              <PressableScale
                key={min}
                haptic
                onPress={() => onChange(service.id, { durationMinutes: min })}
                className="rounded-full px-3.5 py-2"
                style={{
                  backgroundColor: active ? '#FD5001' : th('#fff'),
                  borderWidth: 1,
                  borderColor: active ? '#FD5001' : th('#E5E5E5'),
                  borderCurve: 'continuous',
                }}
              >
                <ThemedText
                  className="text-sm font-semibold"
                  style={{ color: active ? '#fff' : th('#171717') }}
                >
                  {formatDuration(min)}
                </ThemedText>
              </PressableScale>
            );
          })}
          <PressableScale
            haptic
            onPress={() => onChange(service.id, { durationMinutes: null })}
            className="rounded-full px-3.5 py-2"
            style={{
              backgroundColor: !schedulable ? INK : th('#fff'),
              borderWidth: 1,
              borderColor: !schedulable ? INK : th('#E5E5E5'),
              borderCurve: 'continuous',
            }}
          >
            <ThemedText
              className="text-sm font-semibold"
              style={{ color: !schedulable ? '#fff' : th('#737373') }}
            >
              Nenhuma
            </ThemedText>
          </PressableScale>
        </View>
      </View>

      {/* Capacidade (só quando agendável) */}
      {schedulable && (
        <View className="gap-1.5">
          <ThemedText className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Vagas por horário
          </ThemedText>
          <View className="flex-row items-center gap-3">
            <PressableScale
              haptic
              disabled={capacity <= 1}
              onPress={() => onChange(service.id, { slotCapacity: capacity - 1 })}
              className="h-9 w-9 items-center justify-center rounded-full border border-neutral-200"
              style={{ opacity: capacity <= 1 ? 0.4 : 1 }}
            >
              <Ionicons className="text-neutral-900" name="remove" size={18} />
            </PressableScale>
            <ThemedText className="min-w-8 text-center text-lg font-bold text-neutral-900">
              {capacity}
            </ThemedText>
            <PressableScale
              haptic
              onPress={() => onChange(service.id, { slotCapacity: capacity + 1 })}
              className="h-9 w-9 items-center justify-center rounded-full border border-neutral-200"
            >
              <Ionicons className="text-neutral-900" name="add" size={18} />
            </PressableScale>
            <ThemedText className="text-xs text-neutral-400">
              {capacity > 1 ? 'turma em grupo' : '1 = individual'}
            </ThemedText>
          </View>
        </View>
      )}
    </View>
  );
});

/**
 * Atalho para ajustar duração + vagas de vários serviços de uma vez, durante a
 * ativação da agenda. A fonte da configuração continua sendo a tela do próprio
 * serviço; aqui é só o mesmo campo em lote.
 *
 * Controlado: os toques só mexem no rascunho — quem persiste é o Salvar/Ativar.
 */
export function ServiceSchedulingEditor({ value, onChange }: Props) {
  const { data, isLoading } = useProviderServices({ limit: 100 });

  const handleChange = useCallback<Props['onChange']>(
    (id, patch) => onChange(id, patch),
    [onChange],
  );

  // O rascunho vence o que veio do servidor. Serviço intocado mantém a
  // referência original, então só a linha alterada re-renderiza.
  const services = useMemo(() => {
    const list = data?.data ?? [];
    return list.map((service) =>
      value[service.id] ? { ...service, ...value[service.id] } : service,
    );
  }, [data?.data, value]);

  if (isLoading) {
    return (
      <View className="items-center py-8">
        <ActivityIndicator color="#FD5001" />
      </View>
    );
  }

  if (services.length === 0) {
    return (
      <EmptyState
        icon="briefcase-outline"
        title="Nenhum serviço ainda"
        description="Crie um serviço primeiro para poder torná-lo agendável."
      />
    );
  }

  return (
    <View className="gap-2.5">
      {services.map((service) => (
        <ServiceRow key={service.id} service={service} onChange={handleChange} />
      ))}
    </View>
  );
}
